import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Generic type-to-search picker over `options` = [{ value, label, sub }]. */
export const LinkCombobox = ({
  options = [],
  value,
  onChange,
  testId,
  emptyLabel = "Not linked",
  placeholder = "Type to search",
  height = "h-11",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value) || null;

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between rounded-sm ${height} text-base font-normal`}
        >
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
            {selected ? selected.label : emptyLabel}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[min(92vw,26rem)]">
        <Command>
          <CommandInput
            data-testid={`${testId}-search`}
            placeholder={placeholder}
            className="text-base"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Nothing matches that.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="none not linked clear"
                onSelect={() => pick(null)}
                data-testid={`${testId}-none`}
              >
                <Check className={`h-4 w-4 mr-2 shrink-0 ${value ? "opacity-0" : "opacity-100"}`} />
                {emptyLabel}
              </CommandItem>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.sub || ""} ${o.value}`}
                  onSelect={() => pick(o.value)}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <Check
                    className={`h-4 w-4 mr-2 shrink-0 ${
                      value === o.value ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{o.label}</span>
                    {o.sub ? (
                      <span className="block truncate text-xs text-muted-foreground">{o.sub}</span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
